import {useMemo, useId} from 'react'

export function useStableRandom({max, min}: {max?: number; min?: number} = {}) {
  const id = useId()
  const index = useMemo(() => randFromId({id, max, min}), [id, max, min])
  return index
}

/**
 * Creates a seeded random number generator from a seed string.
 * Returns a function (index, max) => number that produces deterministic
 * values in [0, max) for each (seed, index) pair.
 * Use with `useId()` as the seed for SSR-safe randomness.
 */
export function createSeededRandom(seed: string) {
  return (index: number, max: number): number => {
    if (max <= 0) return 0
    const str = `${seed}:${index}`
    let hash = 5381
    for (const char of str) hash = ((hash << 5) + hash + char.charCodeAt(0)) | 0
    return (hash >>> 0) % max
  }
}

function randFromId({max, min, id}: {max?: number; min?: number; id: string}): number {
  let rand = hashId(id)

  if (min && rand < min) {
    rand = min
  }

  if (max) {
    rand = rand % max
  }

  return rand
}

function hashId(id: string): number {
  let hash = 0
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 100
  return (hash % 40) + 40
}
